<template>
  <div>
    <log-modal ref="logModal" />
    <confirmation-modal
      ref="deleteConfirmationModal"
      confirm-button-text="Delete"
      :confirm="deleteRows"
      :message="deleteWarningMessage"
    />
    <business-unit-creation-modal
      ref="businessUnitCreationModal"
      :external-apps="externalApps"
    />
    <div
      id="actionBar"
      class="flex justify-end"
    >
      <div class="flex items-center justify-between p-3">
        <div>
          <button
            class="btn btn-primary-invert"
            @click="save()"
          >
            Save
          </button>
          <button
            class="btn btn-primary-invert"
            @click="openCreationModal()"
          >
            Create
          </button>
          <button
            class="btn btn-primary-invert"
            @click="deleteConfirmation()"
          >
            Delete
          </button>
          <button
            class="btn btn-primary-invert"
            @click="viewLog()"
          >
            View Log
          </button>
          <button
            class="btn btn-primary-invert px-2"
            @click="loadData()"
          >
            Refresh
          </button>
        </div>
      </div>
    </div>

    <ag-grid-vue
      id="businessUnitGrid"
      class="ag-theme-alpine"
      row-drag-managed="true"
      animate-rows="true"
      row-selection="multiple"
      enter-moves-down="true"
      enter-moves-down-after-edit="true"
      stop-editing-when-grid-loses-focus="true"
      :column-defs="columnDefs"
      :row-data="businessUnits"
      :default-col-def="{
        resizable: true,
        sortable: true,
        filter: true,
        floatingFilter: true,
        floatingFilterComponentParams: {suppressFilterButton:true},
        width: 100,
      }"
      :modules="modules"
      @grid-ready="onGridReady"
      @cell-value-changed="onCellValueChanged"
      @sort-changed="onSortChanged"
      @filter-changed="onFilterChanged"
    />
  </div>
</template>

<script>
  import { AgGridVue } from '@ag-grid-community/vue'
  import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';
  import bus                       from '../../event-bus.js'
  import BusinessUnitCreationModal from './BusinessUnitCreationModal.vue'
  import ConfirmationModal from '../../components/shared/ConfirmationModal.vue'
  import LogModal                  from '../shared/LogModal.vue'

  export default {
    components: {
      AgGridVue,
      BusinessUnitCreationModal,
      ConfirmationModal,
      LogModal
    },

    data () {
      return {
        gridApi: null,
        businessUnits: [],
        externalApps: [],
        selectedRowCount: 0,
        modules: [ClientSideRowModelModule]
      }
    },

    computed: {
      columnDefs () {
        return [
           {
            filter: false,
            headerName: "#",
            pinned: 'left',
            sortable: false,
            suppressMovable: true,
            valueGetter: "node.rowIndex + 1",
            width: 50,
           },
           {
            checkboxSelection: true,
            headerCheckboxSelection: true,
            headerCheckboxSelectionFilteredOnly: true,
            pinned: 'left',
            suppressMovable: true,
            sortable: false,
            filter: false,
            width: 50,
          },
          {
            headerName: 'Name',
            field: 'name',
            editable: true,
            width: 250,
          },
          {
            headerName: 'Comment',
            field: 'comment',
            editable: true,
            cellEditor: 'agLargeTextCellEditor',
            width: 300,
          },
          {
            headerName: 'Timezone',
            field: 'timezone',
            editable: true,
            width: 150,
          },
          {
            headerName: 'External App',
            field: 'external_app_id',
            valueFormatter: this.externalAppIdToName,
            editable: true,
            cellEditor: 'agPopupSelectCellEditor',
            cellEditorParams: {values: this.externalApps.map(ea => ea.id)},
            width: 200,
          },
          {
            headerName: 'Is Active',
            field: 'is_active',
            cellRenderer: this.isActiveCheckboxRenderer,
            filterParams: {
              suppressAndOrCondition: true,
              filterOptions: [
                'empty',
                { displayKey: 'active', displayName: 'Active', test: function(filterValue, cellValue) { return cellValue == 'true' }, hideFilterInput: true },
                { displayKey: 'inactive', displayName: 'Inactive', test: function(filterValue, cellValue) { return cellValue == 'false' }, hideFilterInput: true },
              ],
            },
            width: 100,
            cellStyle: { textAlign: "center" },
          },
        ]
      },

      deleteWarningMessage () {
        return "Are you sure you would like to delete " + this.selectedRowCount + " Business Unit(s)."
      }
    },

    created() {
      window.addEventListener("resize", this.gridAutofit)
    },

    destroyed () {
      window.removeEventListener("resize", this.gridAutofit)
    },

    methods: {
      loadData () {
        this.gridApi.showLoadingOverlay()
        this.$http.get('/business_units').then(response => {
          this.businessUnits = response.data
        })

        this.$http.get('/external_apps').then(response => {
          this.externalApps = response.data
        })
      },

      onCellValueChanged (businessUnit) {
        businessUnit.data.edited = true
      },

      getEditedBusinessUnits () {
        return this.businessUnits.filter(businessUnit => businessUnit.edited)
      },

      onGridReady (params) {
        this.gridApi = params.api
        this.loadData()
        this.gridAutofit()
      },

      onSortChanged(params) {
        params.api.refreshCells()
      },

      onFilterChanged(params) {
        params.api.refreshCells()
      },

      isActiveCheckboxRenderer (params) {
        let input = document.createElement('input');
        input.type = "checkbox";
        input.checked = params.value;
        input.addEventListener('click', function (event) {
            params.value = !params.value;
            params.node.data.is_active = params.value;
            params.node.data.edited = true
        });
        return input;
      },

      save () {
        let loadingNoty = this.$noty.loading()
        let params = {
          business_units: this.getEditedBusinessUnits()
        }
        this.$http.post('/business_units/update_records', params).then(response => {
          loadingNoty.close()
          this.$noty.success(response.data.message)
          this.loadData()
        }, error => {
          let message = error.response.data || error.message
          loadingNoty.close()
          this.$noty.error(message)
          this.loadData()
        })
      },

      openCreationModal () {
        this.$refs.businessUnitCreationModal.open()
      },

      deleteConfirmation () {
        let selectedItems = this.gridApi.getSelectedRows();
        if (selectedItems.length > 0) {
          this.selectedRowCount = selectedItems.length
          this.$refs.deleteConfirmationModal.open()
        }
        else {
          this.$noty.error('Please select records to delete')
        }
      },

      deleteRows () {
        let loadingNoty = this.$noty.loading()
        let selectedRowIds = this.gridApi.getSelectedRows().map(row => row.id);
        let params = { business_units: selectedRowIds }
        this.$refs.deleteConfirmationModal.close()
        this.$http.delete('/business_units/delete_records', {params: params}).then(response => {
          loadingNoty.close()
          this.$noty.success(response.data)
          this.loadData()
        }, error => {
          let message = error.response.data || error.message
          loadingNoty.close()
          this.$noty.error(message)
          this.loadData()
        })
      },

      viewLog() {
        let selectedItems = this.gridApi.getSelectedRows();
        if (selectedItems.length === 1) {
          this.$refs.logModal.open(selectedItems[0].id, 'BusinessUnit')
        }
        else {
          this.$noty.error('Please select one record to view log')
        }
      },

      externalAppIdToName (id) {
        let externalApp = this.externalApps.find( ea => ea.id === id.value )
        return externalApp ? externalApp.name : ''
      },

      gridAutofit () {
        if (!document.querySelector('#businessUnitGrid')) return

        document.querySelector('#businessUnitGrid').style.height = (
          document.documentElement.clientHeight
          - document.querySelector('#navbar').offsetHeight
          - document.querySelector('#adminPanelTabs').offsetHeight
          - document.querySelector('#actionBar').offsetHeight
          - document.querySelector('footer').offsetHeight
        ) + 'px'
        this.gridApi.doLayout()
      }
    }
  }
</script>
